/**
 * The credential vault's client half.
 *
 * The type here is the ONLY shape the screens see, and it has no `secret` field
 * on purpose - the server's list and detail responses do not carry one either
 * (see crm-vault/lib/entry.ts). A password exists in this app for exactly as
 * long as one component holds the answer to `revealVaultEntry`, and nothing
 * caches it: no store, no query cache, no `localStorage`.
 */
import { api } from "./api";

/**
 * The kinds of account an entry can be, in the order the filter offers them.
 *
 * Mirrors `VAULT_KINDS` in apps/api/src/modules/crm-vault/lib/entry.ts. It is a
 * mirror rather than a fetch because the labels are i18n keys built off these
 * strings and a menu cannot wait for a request to know its own options - but it
 * IS a mirror, so a kind added on the server and not here is a row this app will
 * draw with a missing label. `GET /vault/status` returns the server's list for
 * exactly that reason: `useVaultStatus` compares them and complains in the
 * console rather than letting the mismatch show up as a blank cell.
 */
export const VAULT_KINDS = ["ftp", "mysql", "cms", "hosting", "domain", "mail", "other"] as const;
export type VaultKind = (typeof VAULT_KINDS)[number];

export interface VaultEntry {
  id: string;
  title: string;
  kind: string;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  host: string | null;
  port: number | null;
  username: string | null;
  url: string | null;
  /** Whether a password is on file. Never the password. */
  hasSecret: boolean;
  hasNotes: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One line of the entry's own history. */
export interface VaultAccess {
  id: string;
  action: string;
  userId: string;
  at: string;
}

export interface VaultStatus {
  /** False when the server has no usable `CRM_VAULT_KEY`. */
  ready: boolean;
  reason: "missing" | "invalid" | null;
  kinds: string[];
}

export interface VaultListResult {
  rows: VaultEntry[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * What a write sends.
 *
 * `secret` and `notes` are three-valued and the difference matters: leave the
 * key OUT to keep the stored value, send `null` to clear it, send a string to
 * replace it. An edit form that always posted its (empty) password field would
 * erase the password on every rename.
 */
export interface VaultInput {
  title?: string;
  kind?: VaultKind;
  clientId?: string | null;
  projectId?: string | null;
  host?: string | null;
  port?: number | null;
  username?: string | null;
  url?: string | null;
  secret?: string | null;
  notes?: string | null;
}

export interface VaultListParams {
  q?: string;
  kind?: string;
  clientId?: string;
  projectId?: string;
  page?: number;
  pageSize?: number;
}

function qs(params: VaultListParams): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === null) continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const getVaultStatus = () => api.get<VaultStatus>("/admin/crm/vault/status");

export const getVaultEntries = (params: VaultListParams = {}) =>
  api.get<VaultListResult>(`/admin/crm/vault${qs(params)}`);

export const getVaultEntry = (id: string) =>
  api.get<{ entry: VaultEntry; access: VaultAccess[] }>(`/admin/crm/vault/${id}`);

export const createVaultEntry = (input: VaultInput) => api.post<VaultEntry>("/admin/crm/vault", input);

export const updateVaultEntry = (id: string, input: VaultInput) =>
  api.patch<VaultEntry>(`/admin/crm/vault/${id}`, input);

export const deleteVaultEntry = (id: string) => api.del<{ ok: true }>(`/admin/crm/vault/${id}`);

/**
 * Read one entry's plaintext. Writes an access row on the server every time.
 *
 * POST rather than GET, because it has that side effect and because a password
 * must not travel in a URL that lands in browser history and every proxy log on
 * the way. Whatever this returns is held in component state and dropped when the
 * dialog closes.
 */
export const revealVaultEntry = (id: string) =>
  api.post<{ secret: string | null; notes: string | null }>(`/admin/crm/vault/${id}/reveal`);
